import {
    Home as HomeIcon,
    Login as LoginIcon,
    ExitToApp as LogoutIcon,
    Person as PersonIcon,
    PersonAdd as PersonAddIcon,
    GridOn as GridIcon,
    Apps as CrosswordIcon,
    RocketLaunch as QuickStartIcon
} from "@mui/icons-material";
import { Avatar, Box, Divider, Drawer, List, ListItem, ListItemIcon, ListItemText, Tooltip, Typography } from "@mui/material";
import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import AuthService from "../services/AuthService";
import Logger from "../services/Logger";
import UserService from "../services/UserService";
import MinimalHeader from "../pages/components/puzzle/MinimalHeader";

const CreatorLayout = ({ children }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const [user, setUser] = useState(null);
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [isAuthenticated, setIsAuthenticated] = useState(false);

    const loadUserData = async () => {
        try {
            const authStatus = await AuthService.isAuthenticated();
            setIsAuthenticated(authStatus);

            if (authStatus) {
                const u = await UserService.getUserInfo();
                setUser(u);
            } else {
                setUser(null);
            }
        } catch (error) {
            Logger.error('Error checking auth or loading user:', error);
            setIsAuthenticated(false);
            setUser(null);
        }
    };

    useEffect(() => {
        loadUserData();
    }, []);

    // Listen for avatar updates
    useEffect(() => {
        const handleAvatarUpdate = () => {
            Logger.log('Avatar updated, refreshing user data...');
            loadUserData();
        };

        window.addEventListener('avatarUpdated', handleAvatarUpdate);

        return () => {
            window.removeEventListener('avatarUpdated', handleAvatarUpdate);
        };
    }, []);

    const handleProfileClick = () => {
        navigate('/account/profile');
        setDrawerOpen(false);
    };

    const handleLogout = async () => {
        try {
            await AuthService.logout();
            setIsAuthenticated(false);
            setUser(null);
        } catch (error) {
            Logger.error('Logout error:', error);
        }
    };

    const handleLogin = async () => {
        try {
            await AuthService.login();
        } catch (error) {
            Logger.error('Login error:', error);
        }
    };

    const handleMenuClick = () => {
        setDrawerOpen(true);
    };

    const handleNavigation = (path) => {
        navigate(path);
        setDrawerOpen(false);
    };

    const handleLogoutFromMenu = () => {
        setDrawerOpen(false);
        if (isAuthenticated) {
            handleLogout();
        } else {
            handleLogin();
        }
    };

    const isCreator = user?.cloudUser?.userRole?.toUpperCase() === 'CREATOR';
    const menuItems = [
        { text: 'Creator home', icon: <HomeIcon />, path: '/creator' },
        ...(!isCreator ? [{ text: 'Crossword', icon: <CrosswordIcon />, path: '/creator/crossword' }] : []),
        { text: 'Grid', icon: <GridIcon />, path: '/creator/grid' },
        { text: 'Quick Start', icon: <QuickStartIcon />, path: '/creator/guide' }
    ];

    const navDisplayName = UserService.getNavDisplayName(user);

    return (
        <Box sx={{ minHeight: '100vh', backgroundColor: '#fafafa' }}>
            <MinimalHeader
                title={process.env.REACT_APP_SITE_NAME || "Puzzle Interact"}
                onMenuClick={handleMenuClick}
            />

            <Box sx={{
                position: 'relative',
                px: { xs: 1, sm: 2 },
                py: 2
            }}>
                {children}
            </Box>

            {/* Navigation Drawer */}
            <Drawer
                anchor="left"
                open={drawerOpen}
                onClose={() => setDrawerOpen(false)}
                sx={{
                    '& .MuiDrawer-paper': {
                        width: 280,
                        boxSizing: 'border-box',
                        backgroundColor: '#fff'
                    }
                }}
            >
                {/* User Info */}
                <Box sx={{
                    p: 3,
                    borderBottom: '1px solid #e0e0e0',
                    backgroundColor: '#f8f9fa',
                    borderRadius: '8px 8px 0 0'
                }}>
                    <Box sx={{ textAlign: 'center' }}>
                        {/* Avatar */}
                        <Box sx={{ mb: 2, display: 'flex', justifyContent: 'center' }}>
                            {!user?.cloudUser ? (
                                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                                    <Avatar
                                        sx={{
                                            width: 60,
                                            height: 60,
                                            backgroundColor: '#6c757d',
                                            border: '2px solid #6c757d'
                                        }}
                                    >
                                        <PersonIcon sx={{ fontSize: 30 }} />
                                    </Avatar>
                                    <Typography variant="subtitle1" sx={{ color: '#6c757d', mt: 1 }}>
                                        Guest
                                    </Typography>
                                </Box>
                            ) : user.cloudUser.details?.avatarUrl ? (
                                <Avatar
                                    src={user.cloudUser.details.avatarUrl}
                                    alt="User Avatar"
                                    sx={{
                                        width: 60,
                                        height: 60,
                                        cursor: 'pointer',
                                        border: '2px solid #3BA666',
                                        '&:hover': {
                                            opacity: 0.8,
                                            transform: 'scale(1.05)',
                                            transition: 'all 0.2s ease-in-out'
                                        }
                                    }}
                                    onClick={handleProfileClick}
                                />
                            ) : (
                                <Tooltip title="Edit your profile" arrow>
                                    <Avatar
                                        sx={{
                                            width: 60,
                                            height: 60,
                                            cursor: 'pointer',
                                            backgroundColor: '#3BA666',
                                            border: '2px solid #3BA666',
                                            '&:hover': {
                                                backgroundColor: '#2d8a4f',
                                                transform: 'scale(1.05)',
                                                transition: 'all 0.2s ease-in-out'
                                            }
                                        }}
                                        onClick={handleProfileClick}
                                    >
                                        <PersonAddIcon sx={{ fontSize: 30 }} />
                                    </Avatar>
                                </Tooltip>
                            )}
                        </Box>

                        {user?.cloudUser && (
                            <>
                                <Typography
                                    variant="subtitle1"
                                    sx={{
                                        fontWeight: 600,
                                        color: '#2d3436',
                                        mb: 1,
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'normal',
                                        maxWidth: '100%',
                                        wordBreak: 'break-all',
                                        lineHeight: 1.3,
                                        minHeight: '2.6em',
                                        display: '-webkit-box',
                                        WebkitLineClamp: 2,
                                        WebkitBoxOrient: 'vertical'
                                    }}
                                    title={navDisplayName}
                                >
                                    {navDisplayName}
                                </Typography>
                            </>
                        )}
                    </Box>
                </Box>

                {/* Navigation Menu */}
                <List sx={{ pt: 1, px: 1 }}>
                    {menuItems.map((item, index) => {
                        // For Grid and Crossword, check if path starts with the item path
                        // For Creator home, check if path is exactly /creator or starts with /creator/ but not /creator/grid or /creator/crossword
                        let isActive;
                        if (item.path === '/creator/grid' || item.path === '/creator/crossword' || item.path === '/creator/guide') {
                            isActive = location.pathname.startsWith(item.path);
                        } else if (item.path === '/creator') {
                            isActive = location.pathname === '/creator' ||
                                (location.pathname.startsWith('/creator/') &&
                                    !location.pathname.startsWith('/creator/grid') &&
                                    !location.pathname.startsWith('/creator/crossword') &&
                                    !location.pathname.startsWith('/creator/guide'));
                        } else {
                            isActive = location.pathname === item.path;
                        }

                        return (
                            <ListItem
                                key={index}
                                component="button"
                                onClick={() => handleNavigation(item.path)}
                                sx={{
                                    backgroundColor: isActive ? '#e8f5e8' : 'transparent',
                                    cursor: 'pointer',
                                    borderRadius: 2,
                                    mb: 0.5,
                                    border: isActive ? '2px solid #3BA666' : '1px solid transparent',
                                    transition: 'all 0.2s ease-in-out',
                                    '&:hover': {
                                        backgroundColor: isActive ? '#d4edda' : '#f8f9fa',
                                        border: isActive ? '2px solid #2d8f47' : '1px solid #e9ecef',
                                        transform: 'translateX(2px)',
                                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                                    },
                                    '&:active': {
                                        transform: 'translateX(1px)',
                                        boxShadow: '0 1px 4px rgba(0,0,0,0.1)'
                                    }
                                }}
                            >
                                <ListItemIcon sx={{
                                    color: isActive ? '#3BA666' : '#6c757d',
                                    minWidth: 40,
                                    transition: 'color 0.2s ease-in-out'
                                }}>
                                    {item.icon}
                                </ListItemIcon>
                                <ListItemText
                                    primary={item.text}
                                    sx={{
                                        color: isActive ? '#3BA666' : '#495057',
                                        fontWeight: isActive ? '600' : '500',
                                        fontSize: '0.95rem',
                                        transition: 'all 0.2s ease-in-out'
                                    }}
                                />
                            </ListItem>
                        );
                    })}
                </List>

                <Divider />

                {/* Login/Logout */}
                <List sx={{ px: 1 }}>
                    <ListItem
                        component="button"
                        onClick={handleLogoutFromMenu}
                        sx={{
                            cursor: 'pointer',
                            borderRadius: 2,
                            mb: 0.5,
                            border: '1px solid transparent',
                            transition: 'all 0.2s ease-in-out',
                            '&:hover': {
                                backgroundColor: '#f8f9fa',
                                border: '1px solid #e9ecef',
                                transform: 'translateX(2px)',
                                boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                            },
                            '&:active': {
                                transform: 'translateX(1px)',
                                boxShadow: '0 1px 4px rgba(0,0,0,0.1)'
                            }
                        }}
                    >
                        <ListItemIcon sx={{
                            color: '#6c757d',
                            minWidth: 40,
                            transition: 'color 0.2s ease-in-out'
                        }}>
                            {isAuthenticated ? <LogoutIcon /> : <LoginIcon />}
                        </ListItemIcon>
                        <ListItemText
                            primary={isAuthenticated ? "Sign out" : "Sign in"}
                            sx={{
                                color: '#495057',
                                fontWeight: '500',
                                fontSize: '0.95rem',
                                transition: 'all 0.2s ease-in-out'
                            }}
                        />
                    </ListItem>
                </List>
            </Drawer>
        </Box>
    );
};

export default CreatorLayout;

